import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Bell } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type Notification = {
  id: string;
  type: string;
  message: string;
  read_at: string | null;
  created_at: string;
  data: {
    request_id?: string;
    book_id?: string;
    sender_id?: string;
    sender_name?: string;
    preview?: string;
    [k: string]: unknown;
  } | null;
};

const ICONS: Record<string, string> = {
  strikes_reset: "🎉",
  strike_added: "⚠️",
  swap_request: "📚",
  swap_accepted: "✅",
  swap_declined: "❌",
  swap_cancelled: "↩️",
  swap_completed: "🎉",
  swap_exchanged: "🎉",
  new_message: "💬",
};


export function NotificationBell() {
  const { user } = useAuth();
  const [items, setItems] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) { setItems([]); return; }
    let cancelled = false;

    const load = async () => {
      const { data } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20);
      if (!cancelled && data) setItems(data as Notification[]);
    };
    load();

    const channel = supabase
      .channel(`notifications-${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        (payload) => setItems((prev) => [payload.new as Notification, ...prev].slice(0, 20)),
      )
      .subscribe();

    return () => { cancelled = true; supabase.removeChannel(channel); };
  }, [user]);

  if (!user) return null;

  const unread = items.filter((n) => !n.read_at).length;

  async function markAllRead() {
    if (!user || unread === 0) return;
    const ids = items.filter((n) => !n.read_at).map((n) => n.id);
    setItems((prev) => prev.map((n) => (ids.includes(n.id) ? { ...n, read_at: new Date().toISOString() } : n)));
    await supabase.from("notifications").update({ read_at: new Date().toISOString() }).in("id", ids);
  }

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(o) => { setOpen(o); if (o) markAllRead(); }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="relative text-white/90 hover:bg-white/10 hover:text-white dark:text-foreground dark:hover:bg-muted"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground ring-2 ring-primary-dark dark:ring-card">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>Notifications</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">No notifications yet.</div>
        ) : (
          <ul className="max-h-80 overflow-y-auto">
            {items.map((n) => {
              const reqId = n.data?.request_id;
              const bookId = n.data?.book_id;
              const senderId = n.data?.sender_id;
              const preview = n.data?.preview;
              const isUnread = !n.read_at;
              const inner = (
                <div className={`flex gap-2 px-3 py-2 text-sm hover:bg-muted ${isUnread ? "bg-primary-soft/40" : ""}`}>
                  <span aria-hidden className="text-base leading-tight">{ICONS[n.type] ?? "🔔"}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`leading-snug ${isUnread ? "font-semibold" : ""}`}>{n.message}</p>
                    {preview && (
                      <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">"{preview}"</p>
                    )}
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {new Date(n.created_at).toLocaleString()}
                    </p>
                  </div>
                  {isUnread && <span aria-hidden className="mt-1 h-2 w-2 shrink-0 rounded-full bg-destructive" />}
                </div>
              );
              let linkEl: React.ReactNode = inner;
              if (n.type === "new_message" && senderId) {
                linkEl = <Link to="/messages" search={{ to: senderId, book: bookId }} onClick={() => setOpen(false)} className="block">{inner}</Link>;
              } else if (n.type === "swap_request" && reqId) {
                linkEl = <Link to="/requests/$id" params={{ id: reqId }} onClick={() => setOpen(false)} className="block">{inner}</Link>;
              } else if (n.type.startsWith("swap_") && bookId) {
                linkEl = <Link to="/book/$id" params={{ id: bookId }} onClick={() => setOpen(false)} className="block">{inner}</Link>;
              } else if (n.type === "strikes_reset" || n.type === "strike_added") {
                linkEl = <Link to="/profile" onClick={() => setOpen(false)} className="block">{inner}</Link>;
              }
              return <li key={n.id}>{linkEl}</li>;
            })}
          </ul>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
